import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UploadSimple, FolderSimple, FileZip, CircleNotch, DownloadSimple, ArrowClockwise, CheckCircle, XCircle, Copy, Clock, Warning, Files, Trash } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EV_TYPES = [
  "clinical", "economic_model", "budget_impact", "rwe", "pro",
  "regulatory", "hta", "reimbursement", "general",
];
const SOURCE_TYPES = ["internal_document", "peer_reviewed", "conference", "regulator", "hta_body", "grey_literature", "web"];
const CONFIDENTIALITY = ["public", "internal", "confidential", "restricted"];
const PRIORITY = ["low", "normal", "high", "critical"];
const ACCEPT = ".pdf,.docx,.xlsx,.pptx,.csv,.ris,.bib,.enw,.xml,.txt,.html,.htm,.json,.jpg,.jpeg,.png,.zip";

const StatusPill = ({ status }) => {
  const map = {
    pending: { icon: Clock, cls: "text-slate-600 bg-slate-100" },
    promoted: { icon: CheckCircle, cls: "text-emerald-700 bg-emerald-50" },
    validated: { icon: CheckCircle, cls: "text-emerald-700 bg-emerald-50" },
    failed: { icon: XCircle, cls: "text-rose-700 bg-rose-50" },
    duplicate: { icon: Copy, cls: "text-amber-700 bg-amber-50" },
    awaiting_review: { icon: Clock, cls: "text-blue-700 bg-blue-50" },
  };
  const meta = map[status] || map.pending;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}>
      <Icon size={10} />{status.replace("_", " ")}
    </span>
  );
};

const CountCard = ({ label, value, icon: Icon, color, testId }) => (
  <div data-testid={testId} className="border border-slate-200 bg-white px-4 py-3">
    <div className="flex items-center gap-1.5 mb-1">
      <Icon size={11} weight="bold" style={{ color }} />
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{label}</span>
    </div>
    <p className="font-mono-data text-2xl font-bold" style={{ color }}>{value}</p>
  </div>
);

export default function BulkUploadPage() {
  const { productId, product } = useProduct();
  const [batchId, setBatchId] = useState(null);
  const [batch, setBatch] = useState(null);
  const [metadata, setMetadata] = useState({
    indication: "", country: "", evidence_type: "general",
    source_type: "internal_document", confidentiality: "confidential",
    review_priority: "normal",
  });
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();
  const folderInputRef = useRef();
  const zipInputRef = useRef();

  const refresh = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/uploads/batches/${id}`);
      setBatch(r.data);
      if (r.data.status === "processing") setProcessing(true);
      if (r.data.status === "completed") setProcessing(false);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!batchId) return;
    const t = setInterval(() => refresh(batchId), 2000);
    refresh(batchId);
    return () => clearInterval(t);
  }, [batchId, refresh]);

  const createBatch = async () => {
    try {
      const r = await axios.post(`${API}/uploads/batches`, { product_id: productId, metadata });
      setBatchId(r.data.id);
      setBatch(r.data);
      toast.success("Batch created — start uploading files");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create batch");
    }
  };

  const upload = async (fileList, kind = "files") => {
    if (!batchId) { toast.error("Create a batch first"); return; }
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      if (kind === "zip") {
        const fd = new FormData();
        fd.append("file", fileList[0]);
        await axios.post(`${API}/uploads/batches/${batchId}/zip`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`ZIP extracted`);
      } else {
        // Chunk into requests of 50 files each
        const arr = Array.from(fileList);
        for (let i = 0; i < arr.length; i += 50) {
          const chunk = arr.slice(i, i + 50);
          const fd = new FormData();
          chunk.forEach(f => {
            // For folder upload, webkitRelativePath is set; pass it as filename
            const relName = f.webkitRelativePath || f.name;
            fd.append("files", f, relName);
          });
          await axios.post(`${API}/uploads/batches/${batchId}/files`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success(`${arr.length} file(s) staged`);
      }
      await refresh(batchId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const process = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/uploads/batches/${batchId}/process`);
      toast.info("Processing started — validating, deduplicating, promoting…");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Process failed");
      setProcessing(false);
    }
  };

  const resume = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/uploads/batches/${batchId}/resume`);
      toast.info("Resumed — picking up files still pending");
      await refresh(batchId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resume failed");
    } finally { setProcessing(false); }
  };

  const downloadErrors = () => {
    window.location.href = `${API}/uploads/batches/${batchId}/errors`;
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    const zips = files.filter(f => f.name.toLowerCase().endsWith(".zip"));
    const rest = files.filter(f => !f.name.toLowerCase().endsWith(".zip"));
    if (zips.length) upload(zips, "zip");
    if (rest.length) upload(rest, "files");
  };

  const c = batch?.counts || { total: 0, completed: 0, failed: 0, duplicates: 0, awaiting_review: 0, pending: 0 };
  const canProcess = batchId && c.pending > 0;

  return (
    <PageShell pageId="bulk-upload" overline={`MAI Knowledge Hub™ · Bulk Upload · ${product?.name || ""}`}
      title="Bulk Document Upload">

      {/* Metadata form */}
      {!batchId && (
        <div data-testid="bulk-metadata-form" className="border border-slate-200 bg-white mb-6">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
            <p className="font-heading font-bold text-sm">Batch metadata</p>
            <p className="text-xs text-slate-500 mt-0.5">Applied to every file in this batch when promoted to evidence records.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-5">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Indication</label>
              <Input data-testid="bulk-metadata-indication" value={metadata.indication}
                     onChange={(e) => setMetadata(m => ({ ...m, indication: e.target.value }))}
                     placeholder="e.g. Severe aortic stenosis" className="mt-1 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Country / Market</label>
              <Input data-testid="bulk-metadata-country" value={metadata.country}
                     onChange={(e) => setMetadata(m => ({ ...m, country: e.target.value }))}
                     placeholder="e.g. India" className="mt-1 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Evidence Type</label>
              <select data-testid="bulk-metadata-evidence-type" value={metadata.evidence_type}
                      onChange={(e) => setMetadata(m => ({ ...m, evidence_type: e.target.value }))}
                      className="w-full mt-1 text-xs border border-slate-300 px-3 py-2 bg-white">
                {EV_TYPES.map(t => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Source Type</label>
              <select data-testid="bulk-metadata-source-type" value={metadata.source_type}
                      onChange={(e) => setMetadata(m => ({ ...m, source_type: e.target.value }))}
                      className="w-full mt-1 text-xs border border-slate-300 px-3 py-2 bg-white">
                {SOURCE_TYPES.map(t => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Confidentiality</label>
              <select data-testid="bulk-metadata-confidentiality" value={metadata.confidentiality}
                      onChange={(e) => setMetadata(m => ({ ...m, confidentiality: e.target.value }))}
                      className="w-full mt-1 text-xs border border-slate-300 px-3 py-2 bg-white">
                {CONFIDENTIALITY.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Review Priority</label>
              <select data-testid="bulk-metadata-review-priority" value={metadata.review_priority}
                      onChange={(e) => setMetadata(m => ({ ...m, review_priority: e.target.value }))}
                      className="w-full mt-1 text-xs border border-slate-300 px-3 py-2 bg-white">
                {PRIORITY.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
            <Button data-testid="bulk-create-batch-btn" onClick={createBatch}
                    className="text-xs bg-slate-900 hover:bg-slate-800 rounded-none">
              Create batch &amp; start uploading
            </Button>
          </div>
        </div>
      )}

      {batchId && (
        <>
          {/* Counts strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="bulk-counts">
            <CountCard label="Total"           value={c.total}           icon={Files} color="#0F2A3D" testId="count-total" />
            <CountCard label="Completed"       value={c.completed}       icon={CheckCircle} color="#047857" testId="count-completed" />
            <CountCard label="Duplicates"      value={c.duplicates}      icon={Copy} color="#B45309" testId="count-duplicates" />
            <CountCard label="Failed"          value={c.failed}          icon={XCircle} color="#B91C1C" testId="count-failed" />
            <CountCard label="Awaiting review" value={c.awaiting_review} icon={Clock} color="#1D4ED8" testId="count-awaiting-review" />
          </div>

          {/* Drop zone */}
          <div data-testid="bulk-drop-zone"
               onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
               onDragLeave={() => setDragOver(false)}
               onDrop={onDrop}
               className={`border-2 border-dashed p-8 text-center bg-white transition-colors ${dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300"}`}>
            <UploadSimple size={28} className="mx-auto text-slate-400 mb-3" />
            <p className="text-sm text-slate-700">Drag files, a folder or a ZIP anywhere in this box</p>
            <p className="text-xs text-slate-500 mt-1">Accepted: PDF, DOCX, XLSX, PPTX, CSV, RIS, BibTeX, EndNote XML, TXT, HTML, JSON, JPG, PNG · Up to 500 files per batch · 50 MB per file</p>
            <div className="flex justify-center gap-2 mt-4">
              <input ref={fileInputRef} type="file" multiple accept={ACCEPT}
                     data-testid="bulk-files-input" className="hidden"
                     onChange={(e) => upload(e.target.files, "files")} />
              <input ref={folderInputRef} type="file" webkitdirectory="" multiple
                     data-testid="bulk-folder-input" className="hidden"
                     onChange={(e) => upload(e.target.files, "files")} />
              <input ref={zipInputRef} type="file" accept=".zip"
                     data-testid="bulk-zip-input" className="hidden"
                     onChange={(e) => upload(e.target.files, "zip")} />
              <Button data-testid="bulk-pick-files-btn" disabled={uploading}
                      onClick={() => fileInputRef.current.click()}
                      className="text-xs bg-slate-900 hover:bg-slate-800 rounded-none">
                <Files size={13} /><span className="ml-1.5">Choose files</span>
              </Button>
              <Button data-testid="bulk-pick-folder-btn" disabled={uploading}
                      onClick={() => folderInputRef.current.click()}
                      className="text-xs rounded-none" variant="outline">
                <FolderSimple size={13} /><span className="ml-1.5">Choose folder</span>
              </Button>
              <Button data-testid="bulk-pick-zip-btn" disabled={uploading}
                      onClick={() => zipInputRef.current.click()}
                      className="text-xs rounded-none" variant="outline">
                <FileZip size={13} /><span className="ml-1.5">Upload ZIP</span>
              </Button>
            </div>
            {uploading && (
              <p className="mt-3 text-xs text-blue-700 flex items-center justify-center gap-1">
                <CircleNotch size={12} className="animate-spin" /> Uploading…
              </p>
            )}
          </div>

          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-between gap-2 mt-6">
            <p className="text-xs text-slate-500">
              Batch <span className="font-mono">{batchId.slice(0, 8)}</span> · Status: <b className="text-slate-800">{batch?.status}</b>
            </p>
            <div className="flex gap-2">
              {canProcess && (
                <Button data-testid="bulk-process-btn" onClick={process} disabled={processing}
                        className="text-xs bg-slate-900 hover:bg-slate-800 rounded-none">
                  {processing ? <CircleNotch size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                  <span className="ml-1.5">Validate &amp; process</span>
                </Button>
              )}
              {c.pending > 0 && !processing && (
                <Button data-testid="bulk-resume-btn" onClick={resume}
                        variant="outline" className="text-xs rounded-none">
                  <ArrowClockwise size={13} /><span className="ml-1.5">Resume</span>
                </Button>
              )}
              {(c.failed + c.duplicates) > 0 && (
                <Button data-testid="bulk-download-errors-btn" onClick={downloadErrors}
                        variant="outline" className="text-xs rounded-none">
                  <DownloadSimple size={13} /><span className="ml-1.5">Download error report</span>
                </Button>
              )}
              <Button data-testid="bulk-new-batch-btn" onClick={() => { setBatchId(null); setBatch(null); }}
                      variant="ghost" className="text-xs rounded-none">
                Start new batch
              </Button>
            </div>
          </div>

          {/* File table */}
          {batch?.files?.length > 0 && (
            <div className="border border-slate-200 bg-white mt-6" data-testid="bulk-file-table">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <p className="font-heading font-bold text-xs uppercase tracking-wide text-slate-700">
                  Files ({batch.files.length})
                </p>
              </div>
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-100">
                    <tr className="text-left uppercase text-[10px] text-slate-600">
                      <th className="px-3 py-2 font-semibold">Filename</th>
                      <th className="px-3 py-2 font-semibold">Ext</th>
                      <th className="px-3 py-2 font-semibold text-right">Size</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Error / Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {batch.files.map((f) => (
                      <tr key={f.id} data-testid={`bulk-file-row-${f.id}`}>
                        <td className="px-3 py-1.5 font-mono text-slate-700 truncate max-w-md">{f.relative_path || f.filename}</td>
                        <td className="px-3 py-1.5 font-mono text-slate-500">{f.extension}</td>
                        <td className="px-3 py-1.5 text-right font-mono-data text-slate-600">
                          {(f.bytes / 1024).toFixed(1)} KB
                        </td>
                        <td className="px-3 py-1.5"><StatusPill status={f.status} /></td>
                        <td className="px-3 py-1.5 text-slate-500 text-[11px]">{f.error || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </PageShell>
  );
}
